// BaseSubscriptionWriteService.js

import { functions, httpsCallable } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const callable = (name) => httpsCallable(functions, name);

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${name} must be a non-empty string`);
  }
}

/**
 * Creates a Stripe setup intent for a base billing customer.
 *
 * @param {object} params - Setup intent input.
 * @param {string} params.baseKey - Base key.
 * @param {string} params.email - Billing email.
 * @param {string} params.name - Billing name.
 *
 * @returns {Promise<object>} Setup intent response.
 */
export async function createBaseSetupIntent({ baseKey, email, name }) {
  assertNonEmptyString(baseKey, 'baseKey');
  assertNonEmptyString(email, 'email');
  assertNonEmptyString(name, 'name');

  const fn = callable('createSetupIntent');
  const { data } = await fn({
    email,
    name,
    ...buildTenantCallableFields(baseKey),
  });

  return data;
}

/**
 * Creates a Stripe subscription for a base.
 *
 * @param {object} params - Subscription input.
 * @param {string} params.baseKey - Base key.
 * @param {string} params.email - Billing email.
 * @param {string} params.name - Billing name.
 * @param {string} params.planId - Stripe Price ID.
 * @param {string} params.planKey - Internal plan key.
 * @param {string} params.paymentMethodId - Stripe payment method ID.
 * @param {string} [params.userKey] - Optional requester user key for audit metadata.
 *
 * @returns {Promise<object>} Subscription response.
 */
export async function createBaseStripeSubscription({
  baseKey,
  email,
  name,
  planId,
  planKey,
  paymentMethodId,
  userKey = '',
}) {
  assertNonEmptyString(baseKey, 'baseKey');
  assertNonEmptyString(email, 'email');
  assertNonEmptyString(name, 'name');
  assertNonEmptyString(planId, 'planId');
  assertNonEmptyString(planKey, 'planKey');
  assertNonEmptyString(paymentMethodId, 'paymentMethodId');

  const fn = callable('createStripeSubscription');
  const { data } = await fn({
    email,
    name,
    paymentMethodId,
    planId,
    planKey,
    userKey: String(userKey || '').trim(),
    ...buildTenantCallableFields(baseKey),
  });

  return data;
}

/**
 * Syncs base billing state from the current Stripe subscription.
 *
 * @param {object} params - Sync input.
 * @param {string} params.baseKey - Base key.
 *
 * @returns {Promise<object>} Sync response.
 */
export async function refreshBaseBillingFromStripe({ baseKey }) {
  assertNonEmptyString(baseKey, 'baseKey');

  const fn = callable('refreshBaseBillingFromStripe');
  const { data } = await fn({ ...buildTenantCallableFields(baseKey) });

  return data;
}

/**
 * Cancels a Stripe subscription for a base.
 *
 * @param {object} params - Cancellation input.
 * @param {string} params.baseKey - Base key.
 * @param {string} [params.subscriptionId] - Optional Stripe subscription ID.
 *
 * @returns {Promise<object>} Cancellation response.
 */
export async function cancelBaseStripeSubscription({
  baseKey,
  subscriptionId = '',
}) {
  assertNonEmptyString(baseKey, 'baseKey');

  const fn = callable('cancelStripeSubscription');
  const { data } = await fn({
    subscriptionId: String(subscriptionId || '').trim(),
    ...buildTenantCallableFields(baseKey),
  });

  return data;
}

/**
 * Creates a Stripe billing page session for a base billing customer.
 *
 * @param {object} params - Billing page input.
 * @param {string} params.baseKey - Base key.
 * @param {string} [params.returnUrl] - Optional absolute URL to return to after page flow.
 *
 * @returns {Promise<object>} Billing page session response.
 */
export async function createBaseBillingPageSession({
  baseKey,
  returnUrl = '',
}) {
  assertNonEmptyString(baseKey, 'baseKey');

  const fn = callable('createBaseBillingPageSession');
  const { data } = await fn({
    returnUrl: String(returnUrl || '').trim(),
    ...buildTenantCallableFields(baseKey),
  });

  return data;
}
